/**
 * The seven Foundations steps (docs/foundations.md §2), on the shared engine
 * (components/tracks/engine.tsx).
 *
 * Each step is a few controls, a drawing, and a Check. Before Check the
 * drawing shows the setup; Check plays the test out of the model — the books
 * land, the current flows, the load climbs — and only then says what
 * happened. Failure is drawn by weight and line, never by colour, because the
 * system has one hue and it means success (styles/tokens.css, "Signal").
 */
import { useState, type ComponentType } from 'react';
import { useLocale } from '../../state/useLocale';
import { num } from '../../lib/format';
import {
  AIRLOCK_M,
  BOOKS_KG_PER_M,
  DEADLINE_S,
  GUESS_STOPS,
  IRIS_PLAN,
  JETPACK_DV,
  LINK_BUDGET,
  LINK_IDS,
  LINK_MAX,
  LINK_TARGET,
  LOADS_LENGTH,
  NO_UPGRADES,
  PLANK_STOPS,
  REQUIRED_FACTOR,
  RESERVE_DV,
  ROPE_STOPS,
  STEP_SHORTS,
  VOLT_STOPS,
  chain,
  checkEstimate,
  jetpack,
  margin,
  powerLine,
  ropeBreakingKn,
  shelf,
  type LinkId,
  type ShelfResult,
  type StepId,
  type Upgrades,
} from '../../lib/foundations';
import { Choice, IrisSays, Shell, Stops, Toggle, at, idx, useLinear, useQ, useTested, useTest, type StepProps } from '../tracks/engine';
import { BRIEFS } from '../../lib/agent';

const clamp = (x: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, x));

/* ── 1. Estimation ─────────────────────────────────────────────────────── */

/** A log scale from 1 W to 10 kW, the guess on it, and the answer sliding in on Check. */
function LogScale({ watts }: { watts: number }) {
  const { tested, p } = useTested();
  const { q } = useQ();
  const x = (w: number) => 24 + (Math.log10(w) / 4) * 272;
  const truth = tested ? 10 ** (2 * p) : 1;
  return (
    <svg className="ffig ffig--scale" viewBox="0 0 320 84" role="img" aria-hidden="true">
      <line className="ffig__axis" x1={24} x2={296} y1={50} y2={50} />
      {[1, 10, 100, 1000, 10000].map((w) => (
        <g key={w}>
          <line className="ffig__axis" x1={x(w)} x2={x(w)} y1={45} y2={55} />
          <text className="ffig__tick" x={x(w)} y={72} textAnchor="middle">
            {q(w, 'W')}
          </text>
        </g>
      ))}
      <g className="ffig__guess" style={{ transform: `translateX(${x(watts)}px)` }}>
        <path d="M 0 44 L -7 30 L 7 30 Z" />
      </g>
      {tested && (
        <g className="ffig__truth" style={{ transform: `translateX(${x(truth)}px)` }}>
          <line x1={0} x2={0} y1={40} y2={60} />
          <circle cx={0} cy={50} r={5} />
        </g>
      )}
    </svg>
  );
}

function Estimate(props: StepProps) {
  const s = useLocale().strings.foundations;
  const { q, times } = useQ();
  const [i, setI] = useState(2);
  const watts = at(GUESS_STOPS, i);
  return (
    <Shell
      {...props}
      id="estimate"
      agent={{ briefs: BRIEFS['foundations:estimate'] ?? [], apply: (p) => { if (p.watts !== undefined) setI(idx(GUESS_STOPS, p.watts as (typeof GUESS_STOPS)[number])); } }}
      text={s.steps.estimate}
      short={STEP_SHORTS.estimate}
      version={String(i)}
      evaluate={() => {
        const r = checkEstimate(watts);
        return {
          ok: r.ok,
          message: r.ok ? s.estimate.close : r.direction === 'low' ? s.estimate.low(times(r.factor)) : s.estimate.high(times(r.factor)),
        };
      }}
    >
      <div className="fsim fsim--estimate">
        <p className="fbig" aria-hidden="true">
          ≈ {q(watts, 'W')}
        </p>
        <LogScale watts={watts} />
        <Stops label={s.estimate.guess} stops={GUESS_STOPS} index={i} onChange={setI} format={(w) => q(w, 'W')} />
      </div>
    </Shell>
  );
}

/* ── 2 and 7. The shelf ────────────────────────────────────────────────── */

/** Millimetres of sag to pixels: exaggerated, so 5 mm reads at a glance. */
const SAG_PX = 2.6;

export function ShelfFigure({ brackets, thickness, result }: { brackets: 2 | 3; thickness: number; result: ShelfResult }) {
  const { tested, p, phase } = useTested();
  const left = 30;
  const right = 290;
  const top = 46;
  // On Check the books are lifted off and dropped back in a stagger, and the
  // plank takes their weight with a little bounce.
  const bounce = tested ? clamp(p * 1.15) + 0.22 * Math.sin(p * Math.PI * 3) * (1 - p) : 1;
  const full = Math.min(result.sagMm * SAG_PX, 60);
  const depth = full * Math.max(0, bounce);
  const t = Math.max(3, thickness / 3);
  const mid = (left + right) / 2;
  const spans: [number, number][] = brackets === 3 ? [[left, mid], [mid, right]] : [[left, right]];
  const curve = spans.map(([a, b]) => `M ${a} ${top} Q ${(a + b) / 2} ${top + 2 * depth} ${b} ${top}`).join(' ');
  const limit = Math.min(result.limitMm * SAG_PX, 60);
  const sagAt = (x: number) => {
    const [a, b] = spans.find(([lo, hi]) => x >= lo && x <= hi) ?? at(spans, 0);
    const u = (x - a) / (b - a);
    return 4 * depth * u * (1 - u);
  };
  const books = [44, 64, 80, 100, 122, 140, 168, 186, 204, 226, 244, 262];
  const supports = brackets === 3 ? [left, mid, right] : [left, right];
  const height = Math.ceil(top + Math.max(full + t, limit + t, 26) + 8);
  const verdict = phase === 'pass' ? 'ffig--ok' : phase === 'fail' ? 'ffig--fail' : '';
  return (
    <svg className={`ffig ffig--shelf ${verdict}`} viewBox={`0 0 320 ${height}`} role="img" aria-hidden="true">
      {books.map((x, k) => {
        const drop = tested ? clamp((p - k * 0.035) / 0.35) : 1;
        return (
          <rect
            key={x}
            className="ffig__book"
            x={x - 7}
            y={top - 20 - (k % 3) * 3 + sagAt(x) - 34 * (1 - drop)}
            opacity={0.25 + 0.75 * drop}
            width={12}
            height={20 + (k % 3) * 3}
          />
        );
      })}
      <path className="ffig__plank" d={curve} strokeWidth={t} fill="none" />
      {spans.map(([a, b]) => (
        <line key={a} className="ffig__limit" x1={a + 20} x2={b - 20} y1={top + limit + t / 2} y2={top + limit + t / 2} />
      ))}
      {supports.map((x) => (
        <path key={x} className="ffig__bracket" d={`M ${x - 9} ${top + t / 2} L ${x + 9} ${top + t / 2} L ${x} ${top + 26} Z`} />
      ))}
    </svg>
  );
}

function ShelfReadout({ result }: { result: ShelfResult }) {
  const s = useLocale().strings.foundations.shelf;
  const { q } = useQ();
  return (
    <p className="fread">
      <strong>{s.sag(q(result.sagMm, 'mm', 1))}</strong> · {s.limit(q(result.limitMm, 'mm', 1))}
    </p>
  );
}

function Loads(props: StepProps) {
  const s = useLocale().strings.foundations;
  const { q } = useQ();
  const [i, setI] = useState(0);
  const thickness = at(PLANK_STOPS, i);
  const result = shelf({ length: LOADS_LENGTH, thickness, brackets: 2 });
  return (
    <Shell
      {...props}
      id="loads"
      agent={{ briefs: BRIEFS['foundations:loads'] ?? [], apply: (p) => { if (p.thickness !== undefined) setI(idx(PLANK_STOPS, p.thickness as (typeof PLANK_STOPS)[number])); } }}
      text={s.steps.loads}
      version={String(i)}
      evaluate={() => ({ ok: result.ok, message: result.ok ? s.shelf.ok : s.shelf.fail })}
    >
      <div className="fsim">
        <p className="fread fread--quiet">
          {s.shelf.alcove(q(LOADS_LENGTH, 'm'))} · {q(BOOKS_KG_PER_M, 'kg/m')}
        </p>
        <ShelfFigure brackets={2} thickness={thickness} result={result} />
        <ShelfReadout result={result} />
        <Stops label={s.shelf.plank} stops={PLANK_STOPS} index={i} onChange={setI} format={(t) => q(t, 'mm')} />
      </div>
    </Shell>
  );
}

function CheckAi(props: StepProps) {
  const s = useLocale().strings.foundations;
  const { q } = useQ();
  const [i, setI] = useState(PLANK_STOPS.indexOf(IRIS_PLAN.thickness as (typeof PLANK_STOPS)[number]));
  const [brackets, setBrackets] = useState<2 | 3>(IRIS_PLAN.brackets);
  const thickness = at(PLANK_STOPS, i);
  const result = shelf({ length: IRIS_PLAN.length, thickness, brackets });
  return (
    <Shell
      {...props}
      id="check-ai"
      agent={{ briefs: BRIEFS['foundations:check-ai'] ?? [], apply: (p) => { if (p.thickness !== undefined) setI(idx(PLANK_STOPS, p.thickness as (typeof PLANK_STOPS)[number])); if (p.brackets) setBrackets(p.brackets); } }}
      text={s.steps['check-ai']}
      version={`${i}:${brackets}`}
      evaluate={() => ({ ok: result.ok, message: result.ok ? s.shelf.ok : s.shelf.fail })}
    >
      <div className="fsim">
        <IrisSays>{s.iris.plan}</IrisSays>
        <p className="fread fread--quiet">
          {s.shelf.alcove(q(IRIS_PLAN.length, 'm', 1))} · {q(BOOKS_KG_PER_M, 'kg/m')}
        </p>
        <ShelfFigure brackets={brackets} thickness={thickness} result={result} />
        <ShelfReadout result={result} />
        <Stops label={s.shelf.plank} stops={PLANK_STOPS} index={i} onChange={setI} format={(t) => q(t, 'mm')} />
        <Choice<2 | 3> label={s.shelf.brackets} options={[2, 3] as const} value={brackets} onChange={setBrackets} />
      </div>
    </Shell>
  );
}

/* ── 3. Power ──────────────────────────────────────────────────────────── */

function LineFigure({ cables, ok, arcs, load }: { cables: number; ok: boolean; arcs: boolean; load: number }) {
  const { tested, p } = useTested();
  const outcome = ok ? 'ok' : arcs ? 'arc' : load > 1000 ? 'gone' : 'hot';
  const state = tested ? outcome : 'idle';
  const arms = [26, 40, 54, 68].slice(0, cables);
  const droop = state === 'hot' ? 16 + 34 * p : 16;
  const wireOpacity = state === 'gone' ? Math.max(0, 1 - p * 1.6) : 1;
  return (
    <svg className={`ffig ffig--line ffig--${state}`} viewBox="0 0 320 130" role="img" aria-hidden="true">
      {[40, 280].map((x) => (
        <g key={x} className="ffig__tower">
          <path d={`M ${x - 12} 124 L ${x} 18 L ${x + 12} 124`} fill="none" />
          {arms.map((y) => (
            <line key={y} x1={x - 16} x2={x + 16} y1={y} y2={y} />
          ))}
        </g>
      ))}
      {arms.map((y) => (
        <path key={y} className="ffig__wire" opacity={wireOpacity} d={`M 56 ${y} Q 160 ${y + droop} 264 ${y}`} fill="none" />
      ))}
      {state === 'gone' &&
        [70, 100, 130, 160, 190, 220, 250].map((x, k) => (
          <circle
            key={x}
            className="ffig__spark"
            cx={x + (k % 2 ? 1 : -1) * 18 * p}
            cy={42 + (k % 3) * 10 + 50 * p * p}
            r={2.5 * (1 - p) + 0.5}
          />
        ))}
      {state === 'arc' && (
        <path
          className="ffig__arc"
          opacity={Math.sin(p * 40) > 0 ? 1 : 0.2}
          d="M 150 30 L 160 44 L 150 52 L 164 64 L 154 72 L 168 88 L 158 98 L 170 118"
          fill="none"
        />
      )}
    </svg>
  );
}

function Power(props: StepProps) {
  const s = useLocale().strings.foundations;
  const { q, times } = useQ();
  const [i, setI] = useState(0);
  const [cables, setCables] = useState(1);
  const v = at(VOLT_STOPS, i);
  const r = powerLine(v, cables);
  const volts = (x: number) => (x >= 1000 ? q(x / 1000, 'kV') : q(x, 'V'));
  return (
    <Shell
      {...props}
      id="power"
      agent={{ briefs: BRIEFS['foundations:power'] ?? [], apply: (p) => { if (p.volts !== undefined) setI(idx(VOLT_STOPS, p.volts as (typeof VOLT_STOPS)[number])); if (p.cables) setCables(p.cables); } }}
      text={s.steps.power}
      short={STEP_SHORTS.power}
      version={`${i}:${cables}`}
      evaluate={() => ({
        ok: r.ok,
        message: r.ok ? s.power.ok : r.arcs ? s.power.arcs : r.load > 1000 ? s.power.vapour : s.power.hot(times(r.load)),
      })}
    >
      <div className="fsim">
        <LineFigure cables={cables} ok={r.ok} arcs={r.arcs} load={r.load} />
        <p className="fread">
          <strong>{s.power.perCable(q(Math.round(r.ampsPerCable), 'A'))}</strong> · {s.power.amps(q(Math.round(r.amps), 'A'))}
        </p>
        <Stops label={s.power.volts} stops={VOLT_STOPS} index={i} onChange={setI} format={volts} />
        <Choice<number> label={s.power.cables} options={[1, 2, 3, 4] as const} value={cables} onChange={setCables} />
      </div>
    </Shell>
  );
}

/* ── 4. Margin ─────────────────────────────────────────────────────────── */

/** A hoist, a rope and a weight; Check jolts the load up to five times its weight. */
function HoistFigure({ diameter, factor, ok }: { diameter: number; factor: number; ok: boolean }) {
  const { tested, p } = useTested();
  const { times } = useQ();
  const jolt = tested ? 1 + (REQUIRED_FACTOR - 1) * p : 1;
  const snapped = tested && !ok && jolt >= factor;
  const fall = snapped ? clamp((jolt - factor) / 1.2) : 0;
  const stretch = Math.min(1, jolt / factor) * 6;
  const ropeW = Math.max(1.5, diameter / 2.2);
  const held = tested && ok && p >= 1;
  return (
    <svg className={`ffig ffig--hoist${held ? ' ffig--ok' : ''}`} viewBox="0 0 320 150" role="img" aria-hidden="true">
      <rect className="ffig__beam" x={70} y={8} width={180} height={10} />
      <line className="ffig__axis" x1={60} x2={260} y1={141} y2={141} />
      {snapped ? (
        <>
          <line className="ffig__rope" x1={160} x2={160} y1={18} y2={54} strokeWidth={ropeW} />
          <g style={{ transform: `translateY(${fall * (140 - 118 - stretch)}px)` }}>
            <line className="ffig__rope" x1={160} x2={160} y1={62} y2={84 + stretch} strokeWidth={ropeW} />
            <rect className="ffig__weight" x={130} y={84 + stretch} width={60} height={34} />
          </g>
        </>
      ) : (
        <>
          <line className="ffig__rope" x1={160} x2={160} y1={18} y2={84 + stretch} strokeWidth={ropeW} />
          <rect className="ffig__weight" x={130} y={84 + stretch} width={60} height={34} />
        </>
      )}
      <text className="ffig__label ffig__label--big" x={20} y={100}>
        ×{times(jolt)}
      </text>
    </svg>
  );
}

function Margin(props: StepProps) {
  const s = useLocale().strings.foundations;
  const { q, times } = useQ();
  const [i, setI] = useState(0);
  const d = at(ROPE_STOPS, i);
  const r = margin(d);
  const max = ropeBreakingKn(at(ROPE_STOPS, ROPE_STOPS.length - 1));
  const pct = (kn: number) => `${Math.min(100, (kn / max) * 100)}%`;
  return (
    <Shell
      {...props}
      id="margin"
      agent={{ briefs: BRIEFS['foundations:margin'] ?? [], apply: (p) => { if (p.d !== undefined) setI(idx(ROPE_STOPS, p.d as (typeof ROPE_STOPS)[number])); } }}
      text={s.steps.margin}
      version={String(i)}
      evaluate={() => ({ ok: r.ok, message: r.ok ? s.margin.ok : s.margin.fail(times(r.factor)) })}
    >
      <div className="fsim">
        <HoistFigure diameter={d} factor={r.factor} ok={r.ok} />
        <div className="fbars">
          <div className="fbars__row">
            <span className="fbars__label">{s.margin.load(q(r.loadKn, 'kN', 1))}</span>
            <span className="fbars__track">
              <span className="fbars__fill fbars__fill--load" style={{ width: pct(r.loadKn) }} />
            </span>
          </div>
          <div className="fbars__row">
            <span className="fbars__label">{s.margin.breaks(q(r.breakingKn, 'kN', 1))}</span>
            <span className="fbars__track">
              <span className="fbars__fill" style={{ width: pct(r.breakingKn) }} />
              <span className="fbars__mark" style={{ insetInlineStart: pct(r.loadKn * REQUIRED_FACTOR) }} aria-hidden="true">
                ×{REQUIRED_FACTOR}
              </span>
            </span>
          </div>
        </div>
        <p className="fread">
          <strong>{s.margin.factor(times(r.factor))}</strong>
        </p>
        <Stops label={s.margin.rope} stops={ROPE_STOPS} index={i} onChange={setI} format={(v) => q(v, 'mm')} />
      </div>
    </Shell>
  );
}

/* ── 5. Weakest link ───────────────────────────────────────────────────── */

const CHAIN_MAX = 60;

function ChainRows({ up, change }: { up: Upgrades; change(id: LinkId, delta: number): void }) {
  const s = useLocale().strings.foundations;
  const { q } = useQ();
  const { locale } = useLocale();
  const { tested, p, phase } = useTested();
  const r = chain(up);
  const left = LINK_BUDGET - r.spent;
  // The load test: the load climbs to the target, or to the weakest part's
  // strength, whichever comes first, and that part lets go.
  const peak = r.ok ? LINK_TARGET : r.holdsKn;
  const load = tested ? peak * p : 0;
  const busy = phase === 'testing';
  return (
    <>
      <p className="fread fread--quiet">
        {s.chain.target(q(LINK_TARGET, 'kN'))} · {s.chain.left(left, LINK_BUDGET)}
        {tested && (
          <>
            {' '}
            · <strong className="fload">{q(Math.round(load), 'kN')}</strong>
          </>
        )}
      </p>
      <ul className="fchain">
        {LINK_IDS.map((id) => {
          const broken = tested && !r.ok && r.breaks === id && p >= 0.98;
          return (
            <li key={id} className={broken ? 'is-broken' : ''}>
              <span className="fchain__name">{s.chain.parts[id]}</span>
              <span className="fbars__track">
                <span className="fbars__fill" style={{ width: `${(r.strengths[id] / CHAIN_MAX) * 100}%` }} />
                {tested && (
                  <span
                    className="fbars__fill fbars__fill--test"
                    style={{ width: `${(Math.min(load, r.strengths[id]) / CHAIN_MAX) * 100}%` }}
                  />
                )}
                <span className="fbars__target" style={{ insetInlineStart: `${(LINK_TARGET / CHAIN_MAX) * 100}%` }} aria-hidden="true" />
              </span>
              <span className="fchain__kn">{q(r.strengths[id], 'kN')}</span>
              <span className="fchain__ctl">
                <button type="button" className="btn btn-quiet" disabled={busy || up[id] === 0} onClick={() => change(id, -1)}>
                  {s.chain.undo}
                </button>
                <button type="button" className="btn" disabled={busy || left === 0 || up[id] === LINK_MAX} onClick={() => change(id, 1)}>
                  {s.chain.upgrade} {up[id] > 0 ? `(${num(up[id], locale)})` : ''}
                </button>
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
}

function Weakest(props: StepProps) {
  const s = useLocale().strings.foundations;
  const { q } = useQ();
  const [up, setUp] = useState<Upgrades>(NO_UPGRADES);
  const change = (id: LinkId, delta: number) =>
    setUp((prev) => ({ ...prev, [id]: Math.max(0, Math.min(LINK_MAX, prev[id] + delta)) }));
  return (
    <Shell
      {...props}
      id="weakest"
      agent={{ briefs: BRIEFS['foundations:weakest'] ?? [], apply: (p) => { if (p.up) setUp({ ...p.up }); } }}
      text={s.steps.weakest}
      version={LINK_IDS.map((id) => up[id]).join('')}
      evaluate={() => {
        const res = chain(up);
        return { ok: res.ok, message: res.ok ? s.chain.ok(q(LINK_TARGET, 'kN')) : s.chain.broke[res.breaks](q(res.holdsKn, 'kN')) };
      }}
    >
      <div className="fsim">
        <ChainRows up={up} change={change} />
      </div>
    </Shell>
  );
}

/* ── 6. Trade-off ──────────────────────────────────────────────────────── */

const SPEED_STOPS = [2, 3, 4, 5, 6, 6.5, 7, 7.5, 8, 9, 10, 11] as const;

/** The flight to the airlock, played at twenty times real speed. */
function FlightFigure({ speed, kit }: { speed: number; kit: boolean }) {
  const { phase, run } = useTest();
  const { q } = useQ();
  const r = jetpack(speed, kit);
  const tested = phase !== 'idle';
  // Long enough to see the door close on a slow plan: 26 simulated seconds.
  const SIM = 26;
  const p = useLinear(phase === 'testing', run);
  const t = tested ? p * SIM : 0;
  const overshoots = r.broken === 'fuel';
  const pos = Math.min(speed * t, overshoots ? AIRLOCK_M * 1.25 : AIRLOCK_M);
  const x = (m: number) => 20 + (m / AIRLOCK_M) * 250;
  const doorShut = clamp((t - DEADLINE_S) / 1.5);
  return (
    <svg className="ffig ffig--flight" viewBox="0 0 320 90" role="img" aria-hidden="true">
      <line className="ffig__axis" x1={20} x2={270} y1={48} y2={48} strokeDasharray="2 4" />
      <rect className="ffig__station" x={272} y={18} width={40} height={56} />
      <rect className="ffig__door" x={272} y={30} width={6} height={32 * doorShut} />
      <g style={{ transform: `translate(${x(pos)}px, 48px)` }}>
        <circle className="ffig__astro" r={7} />
        {kit && <rect className="ffig__kit" x={-17} y={-4} width={8} height={8} />}
        {phase === 'testing' && <path className="ffig__thrust" d="M -9 5 L -20 0 L -9 -5" />}
      </g>
      <text className="ffig__label" x={20} y={20}>
        {q(Math.min(t, SIM), 's', 1)}
      </text>
      <text className="ffig__label" x={292} y={86} textAnchor="middle">
        {q(DEADLINE_S, 's')}
      </text>
    </svg>
  );
}

function Tradeoff(props: StepProps) {
  const s = useLocale().strings.foundations;
  const { q } = useQ();
  const [i, setI] = useState(2);
  const [kit, setKit] = useState(false);
  const speed = at(SPEED_STOPS, i);
  const r = jetpack(speed, kit);
  const unit = 'm/s';
  const fmt = (v: number) => q(v, unit, Number.isInteger(v) ? 0 : 1);
  const message = (): string => {
    switch (r.broken) {
      case null:
        return s.jetpack.ok;
      case 'kit':
        return s.jetpack.kitMissing;
      case 'fuel':
        return s.jetpack.fuel;
      case 'reserve':
        return s.jetpack.reserve(q(r.reserve, unit, 1));
      case 'late':
        return s.jetpack.late(q(r.seconds, 's', 1));
    }
  };
  const pct = (v: number) => `${clamp(v / JETPACK_DV) * 100}%`;
  return (
    <Shell
      {...props}
      id="tradeoff"
      agent={{ briefs: BRIEFS['foundations:tradeoff'] ?? [], apply: (p) => { if (p.speed !== undefined) setI(idx(SPEED_STOPS, p.speed as (typeof SPEED_STOPS)[number])); if (p.kit !== undefined) setKit(p.kit); } }}
      text={s.steps.tradeoff}
      short={STEP_SHORTS.tradeoff}
      version={`${i}:${kit}`}
      evaluate={() => ({ ok: r.ok, message: message() })}
    >
      <div className="fsim">
        <FlightFigure speed={speed} kit={kit} />
        <div className="fbars">
          <div className="fbars__row">
            <span className="fbars__label">{s.jetpack.budget(q(r.budget, unit, 1))}</span>
            <span className="fbars__track">
              <span className="fbars__fill fbars__fill--load" style={{ width: pct(Math.min(r.used, r.budget)) }} />
              <span
                className="fbars__fill fbars__fill--rest"
                style={{ insetInlineStart: pct(Math.min(r.used, r.budget)), width: pct(Math.max(0, r.reserve)) }}
              />
              <span className="fbars__target" style={{ insetInlineStart: pct(r.budget - RESERVE_DV) }} aria-hidden="true" />
            </span>
          </div>
        </div>
        <p className="fread">
          <strong>{s.jetpack.arrive(q(r.seconds, 's', 1))}</strong> · {s.jetpack.used(q(r.used, unit))} · {q(AIRLOCK_M, 'm')}
        </p>
        <Stops label={s.jetpack.speed} stops={SPEED_STOPS} index={i} onChange={setI} format={fmt} />
        <Toggle label={s.jetpack.kit} checked={kit} onChange={setKit} />
      </div>
    </Shell>
  );
}

export const STEPS: Record<StepId, ComponentType<StepProps>> = {
  estimate: Estimate,
  loads: Loads,
  power: Power,
  margin: Margin,
  weakest: Weakest,
  tradeoff: Tradeoff,
  'check-ai': CheckAi,
};
